<template>
  <div class="menu-management-container">
    <div class="content-wrapper">
      <div class="table-toolbar">
        <span class="page-title">菜单管理</span>
        <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd(null)">新增菜单</el-button>
      </div>

      <!-- 菜单树 -->
      <el-table
        v-loading="tableLoading"
        :data="treeData"
        row-key="id"
        :tree-props="{ children: 'children' }"
        border
        stripe
        style="width: 100%"
        default-expand-all
      >
        <el-table-column prop="menuName" label="菜单名称" min-width="200" align="left" />
        <el-table-column prop="menuType" label="类型" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="typeTagMap[row.menuType]" size="small">
              {{ typeLabelMap[row.menuType] }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="path" label="路由路径" min-width="180" align="center" show-overflow-tooltip />
        <el-table-column prop="component" label="组件" min-width="200" align="center" show-overflow-tooltip />
        <el-table-column prop="perms" label="权限标识" min-width="160" align="center" show-overflow-tooltip />
        <el-table-column prop="sortOrder" label="排序" width="80" align="center" />
        <el-table-column label="操作" width="200" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="handleAdd(row)">新增下级</el-button>
            <el-button type="text" size="small" @click="handleEdit(row)">编辑</el-button>
            <el-button type="text" size="small" style="color: #F56C6C" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="formTitle"
      :visible.sync="formVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form :model="formData" :rules="rules" label-width="100px" ref="formRef">
        <el-form-item label="上级菜单">
          <el-cascader
            v-model="formData.parentId"
            :options="parentMenuOptions"
            :props="{ value: 'id', label: 'menuName', checkStrictly: true, emitPath: false }"
            placeholder="顶级菜单"
            clearable
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="菜单名称" prop="menuName">
          <el-input v-model="formData.menuName" placeholder="请输入菜单名称" />
        </el-form-item>
        <el-form-item label="类型" prop="menuType">
          <el-radio-group v-model="formData.menuType">
            <el-radio :label="1">目录</el-radio>
            <el-radio :label="2">菜单</el-radio>
            <el-radio :label="3">按钮</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="路由路径" v-if="formData.menuType !== 3">
          <el-input v-model="formData.path" placeholder="如: /member" />
        </el-form-item>
        <el-form-item label="组件路径" v-if="formData.menuType === 2">
          <el-input v-model="formData.component" placeholder="如: views/member/index" />
        </el-form-item>
        <el-form-item label="权限标识" v-if="formData.menuType === 3">
          <el-input v-model="formData.perms" placeholder="如: member:export" />
        </el-form-item>
        <el-form-item label="图标">
          <el-input v-model="formData.icon" placeholder="如: el-icon-user" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="formData.sortOrder" :min="0" />
        </el-form-item>
        <el-form-item label="是否可见">
          <el-switch v-model="formData.visible" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="formVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="handleSubmit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getMenuTree, addMenu, updateMenu, deleteMenu } from '@/api/menu'

export default {
  name: 'MenuManagement',
  data() {
    return {
      tableLoading: false,
      submitLoading: false,
      treeData: [],
      formVisible: false,
      formTitle: '新增菜单',
      formData: {
        id: null,
        parentId: null,
        menuName: '',
        menuType: 2,
        path: '',
        component: '',
        perms: '',
        icon: '',
        sortOrder: 0,
        visible: 1
      },
      rules: {
        menuName: [{ required: true, message: '请输入菜单名称', trigger: 'blur' }],
        menuType: [{ required: true, message: '请选择类型', trigger: 'change' }]
      },
      typeTagMap: { 1: '', 2: 'success', 3: 'warning' },
      typeLabelMap: { 1: '目录', 2: '页面', 3: '按钮' }
    }
  },
  computed: {
    // 上级菜单下拉选项（只显示目录和菜单类型）
    parentMenuOptions() {
      return this.treeData.filter(m => m.menuType !== 3)
    }
  },
  created() {
    this.getList()
  },
  methods: {
    // 获取菜单树
    getList() {
      this.tableLoading = true
      getMenuTree().then(res => {
        this.treeData = res.data || []
      }).catch(() => {}).finally(() => {
        this.tableLoading = false
      })
    },

    // 新增
    handleAdd(parent) {
      this.formTitle = parent ? `新增「${parent.menuName}」的下级菜单` : '新增顶级菜单'
      this.formData = {
        id: null,
        parentId: parent ? parent.id : null,
        menuName: '',
        menuType: 2,
        path: '',
        component: '',
        perms: '',
        icon: '',
        sortOrder: 0,
        visible: 1
      }
      this.formVisible = true
    },

    // 编辑
    handleEdit(row) {
      this.formTitle = '编辑菜单'
      this.formData = {
        id: row.id,
        parentId: row.parentId,
        menuName: row.menuName,
        menuType: row.menuType,
        path: row.path || '',
        component: row.component || '',
        perms: row.perms || '',
        icon: row.icon || '',
        sortOrder: row.sortOrder || 0,
        visible: row.visible !== undefined ? row.visible : 1
      }
      this.formVisible = true
    },

    // 删除
    handleDelete(row) {
      this.$confirm(`确定要删除「${row.menuName}」吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        deleteMenu(row.id).then(() => {
          this.$message.success('删除成功')
          this.getList()
        }).catch(() => {
          this.$message.error('删除失败，可能有子菜单')
        })
      }).catch(() => {})
    },

    // 提交
    handleSubmit() {
      this.$refs.formRef.validate(valid => {
        if (!valid) return
        this.submitLoading = true
        const api = this.formData.id ? updateMenu(this.formData) : addMenu(this.formData)
        api.then(() => {
          this.$message.success('操作成功')
          this.formVisible = false
          this.getList()
        }).catch(() => {
          this.$message.error('操作失败')
        }).finally(() => {
          this.submitLoading = false
        })
      })
    }
  }
}
</script>

<style lang="less" scoped>
.menu-management-container {
  padding: 20px;
}
</style>
